import { OpRawMenuItemType } from 'config/createRawMenuItems';
import { validateScopes } from 'utils/scopes';

/**
 * Walks the visitor sidebar menu (including nested children) in display order
 * and returns the relative route of the first page the user is allowed to
 * access based on their active scopes. Mirrors invisit-web's
 * `getFirstAvailableRoute` so a user landing on a page they lack scope for is
 * redirected to their first accessible page instead of hitting a 404 or an
 * infinite loader.
 *
 * @param menuItems - the visitor sidebar items (from `sidebarItems`)
 * @param activeScopes - the user's active scopes for the current org
 * @param orgId - the current org id (used to inject into scope templates)
 * @returns the first accessible relative route, or `null` if none.
 */
export const getFirstAvailableVisitorRoute = (
  menuItems: OpRawMenuItemType[],
  activeScopes: string[],
  orgId: number,
): string | null => {
  // Flatten to leaf items (those with a concrete route) in display order.
  const leaves: OpRawMenuItemType[] = [];
  const collect = (items: OpRawMenuItemType[]) => {
    items.forEach((item) => {
      if (item.items?.length) {
        collect(item.items);
      } else if (item.route) {
        leaves.push(item);
      }
    });
  };
  collect(menuItems);

  const accessibleLeaf = leaves.find((leaf) => {
    // Only redirect to pages we can prove the user has scope for.
    if (!leaf.scope?.length) {
      return false;
    }
    return validateScopes({
      activeScopes,
      orgId,
      rawAllowedScopes: leaf.scope,
    });
  });

  return accessibleLeaf?.route ?? null;
};
